import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';

export interface CompositeSelectControlInfoType {
  info: {
    topLabelText: string;
    options: { name: string; value: string }[];
    isOptionsLoading: boolean;
    placeHolderText: string;
    disabled: boolean;
    topLabelStyle: {
      fontFamily: string;
      fontWeight: number;
      fontSize: string;
      color: string;
      lineHeight: string;
      bottomMargin: string;
    };
    optionStyle: {
      fontFamily: string;
      fontWeight: number;
      fontSize: string;
      color: string;
      lineHeight: string;
      placeHolderColor: string;
      isPlaceHolderItalic: boolean;
      borderColor: string;
      borderWidth: string;
      height: string;
      loadingIndicatorTopMargin: string;
    };
    selectionChangeHandler?: (code: string) => void;
  };
}

export const CompositeSelectControl = (props: CompositeSelectControlInfoType) => {
  const ITEM_HEIGHT = 120;
  const ITEM_PADDING_TOP = 8;
  const [selectedOptionValue, setSelectedOptionValue] = React.useState('');
  // This is be executed when the state changes
  useEffect(() => {
    if (selectedOptionValue && props.info.selectionChangeHandler) {
      props.info.selectionChangeHandler(selectedOptionValue);
    }
  }, [props.info, selectedOptionValue]);

  const handleSelectionChange = (event: SelectChangeEvent) => {
    setSelectedOptionValue(event.target.value);
    if (selectedOptionValue.length > 0 && props.info.selectionChangeHandler) {
      props.info.selectionChangeHandler(selectedOptionValue);
    }
  };

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      },
    },
  };

  function getStyles(option: string, selectedOption: string) {
    return {
      ...props.info.optionStyle,
      color: props.info.optionStyle.color,
      fontWeight: selectedOption === option.toString() ? 800 : 400,
      fontFamily: props.info.optionStyle.fontFamily,
    };
  }

  const loadingIndicator = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
        marginLeft: '43%',
        marginTop: props.info.optionStyle.loadingIndicatorTopMargin,
      }}
    >
      <CircularProgress sx={{ color: 'grey' }} size={25} thickness={4} />
    </Box>
  );

  const placeHolder = (
    <div style={{ verticalAlign: 'middle', display: 'table-cell', margin: 'auto' }}>
      <Typography
        sx={{
          ...props.info.optionStyle,
          color: props.info.optionStyle.placeHolderColor,
          fontStyle: props.info.optionStyle.isPlaceHolderItalic ? 'italic' : '',
          opacity: '.7',
          textAlign: 'left',
          paddingTop: '2rem',
          height: '64px',
        }}
      >
        {props.info.placeHolderText}
      </Typography>
    </div>
  );

  const SelectControl = (
    <Box sx={{ width: '100%' }}>
      <Grid item container direction={'column'}>
        <Grid item>
          <Typography
            gutterBottom
            sx={{
              ...props.info.topLabelStyle,
            }}
          >
            {props.info.topLabelText}
          </Typography>
        </Grid>
        <Grid item sx={{ position: 'relative' }}>
          {props.info.isOptionsLoading && loadingIndicator}
          <Select
            displayEmpty
            value={selectedOptionValue}
            onChange={handleSelectionChange}
            input={<OutlinedInput />}
            defaultValue={''}
            MenuProps={MenuProps}
            disabled={props.info.disabled}
            inputProps={{ 'aria-label': 'Without label' }}
            sx={{
              fontFamily: props.info.optionStyle.fontFamily,
              color: props.info.optionStyle.color,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: props.info.optionStyle.borderColor,
                borderWidth: props.info.optionStyle.borderWidth,
              },
              '& .MuiSvgIcon-root': {
                color: props.info.optionStyle.borderColor,
              },
              width: '100%',
              height: props.info.optionStyle.height,
            }}
          >
            <MenuItem disabled value="">
              {!props.info.isOptionsLoading && placeHolder}
            </MenuItem>

            {!props.info.isOptionsLoading &&
              props.info.options &&
              props.info.options.map((option) => (
                <MenuItem key={option.name} value={option.value} style={getStyles(option.value, selectedOptionValue)}>
                  {option.name}
                </MenuItem>
              ))}
          </Select>
        </Grid>
      </Grid>
    </Box>
  );
  return SelectControl;
};
